/**
 * The allocate dialog's row model, its previews and its write plan (TMMAPP-0109, TMMAPI-0145 /
 * MM-D-113 / 125). A row is one destination — a virtual account's Unassigned or one of its
 * envelopes — with a category and an amount, a percent of the bank line's OPERATING amount, or
 * "the rest" (one per line). The previews mirror the server's rule — fixed first, then each percent
 * `floor(|base| × bp / 10000)` with the base's sign, then the rest — and are previews only: the
 * server resolves, and its `resolved[]` replaces them after a `400`.
 *
 * Parts the dialog cannot edit (an archived envelope, a virtual account the caller does not own
 * or that is not listed) are FIXED: shown read-only, counted as published, and their virtual
 * account is never written. Writes never reverse and re-post (the 0109 amendment): a virtual
 * account the line is new to is one `post`; one it is already in is a `total` (when the group's
 * total changed) then a `split` of its parts; one whose rows were all removed is a `total` to zero.
 * The server's bases differ from the dialog's one base (a split's percent is of the virtual
 * account total; a post's rest ignores the line's other parts), so sliced rows go out as the
 * previewed amounts whenever the line holds anything else; only a wholly new line sends the typed
 * percent / rest.
 */
import type { AllocationLineRequest, SplitPartRequest } from '@/api/allocations';
import type { AllocationLine, OwnerRegisterLine } from '@/api/transactions';
import { generateRequestId } from '@/utils/requestId';

export type AllocateMode = 'amount' | 'percent' | 'rest';

/**
 * A row's "Uncategorized" choice (TMMAPP-0114 B5): it sends `uncategorized: true`, so the part lands
 * with no category. A row with no choice (`''`) sends no category and takes the fallback — the
 * payee's default on a post (MM-D-126), the inherited category on a re-split.
 */
export const UNCATEGORIZED = 'none';

export interface AllocateRow {
  key: string;
  virtualAccountId: number;
  /** Null for the virtual account's Unassigned row (MM-D-112). */
  envelopeId: number | null;
  /** A category id, `UNCATEGORIZED`, or `''` (no choice: the server's fallback). */
  categoryId: number | '' | typeof UNCATEGORIZED;
  mode: AllocateMode;
  /** The typed amount or percent; empty for the rest. */
  value: string;
  /** The live part this row started from, when the dialog opened on an allocated line. */
  allocationId: number | null;
}

/** A row's typed value as the wire would take it: a signed amount, basis points, or the rest. */
export type ParsedSlice =
  | { kind: 'amount'; amountMinor: number }
  | { kind: 'percent'; percentBp: number }
  | { kind: 'rest' }
  | null;

/** A live part with the virtual account it resolves to (null: unknown to the caller). */
export interface PlacedPart {
  part: AllocationLine;
  virtualAccountId: number | null;
}

export function newRow(
  virtualAccountId: number,
  envelopeId: number | null,
  partial: Partial<Omit<AllocateRow, 'key' | 'virtualAccountId' | 'envelopeId'>> = {},
): AllocateRow {
  return {
    key: generateRequestId(),
    virtualAccountId,
    envelopeId,
    categoryId: '',
    mode: 'amount',
    value: '',
    allocationId: null,
    ...partial,
  };
}

/** The virtual account a part sits in: served, else through its envelope's owner. */
export function partVirtualAccount(
  part: AllocationLine,
  envelopeOwner: (envelopeId: number) => number | undefined,
): number | null {
  return (
    part.virtualAccountId ??
    (part.envelopeId !== null ? (envelopeOwner(part.envelopeId) ?? null) : null)
  );
}

/**
 * The line's parts, split into the ones the dialog can edit (a listed destination of a known
 * virtual account) and the fixed ones it only shows.
 */
export function partitionParts(
  line: Pick<OwnerRegisterLine, 'allocations'>,
  editableDestination: (virtualAccountId: number, envelopeId: number | null) => boolean,
  envelopeOwner: (envelopeId: number) => number | undefined,
): { editable: PlacedPart[]; fixed: PlacedPart[] } {
  const editable: PlacedPart[] = [];
  const fixed: PlacedPart[] = [];
  for (const part of line.allocations) {
    const virtualAccountId = partVirtualAccount(part, envelopeOwner);
    const placed = { part, virtualAccountId };
    const target = part.unassigned ? null : part.envelopeId;
    if (virtualAccountId !== null && editableDestination(virtualAccountId, target)) {
      editable.push(placed);
    } else {
      fixed.push(placed);
    }
  }
  return { editable, fixed };
}

/** The rows an allocated line opens with: one per editable live part, as amounts. */
export function rowsFromParts(
  editable: readonly PlacedPart[],
  minorToText: (minor: number) => string,
): AllocateRow[] {
  return editable.flatMap(({ part, virtualAccountId }) =>
    virtualAccountId === null
      ? []
      : [
          newRow(virtualAccountId, part.unassigned ? null : part.envelopeId, {
            categoryId: part.categoryId ?? UNCATEGORIZED,
            value: minorToText(Math.abs(part.amountMinor)),
            allocationId: part.id,
          }),
        ],
  );
}

/** Basis points from a typed percent ("12.5" → 1250); null when not a percent in 0.01–100. */
export function parsePercentBp(text: string): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (!/^\d+(\.\d{0,2})?$/.test(trimmed)) return null;
  const bp = Math.round(Number(trimmed) * 100);
  return bp >= 1 && bp <= 10000 ? bp : null;
}

/**
 * The server's rule, as a preview: null where a row is empty or unparsable. `fixedMinor` is the
 * sum of the parts the dialog cannot edit; the rest leaves them alone too.
 */
export function previewAmounts(
  rows: readonly AllocateRow[],
  parsed: readonly ParsedSlice[],
  baseMinor: number,
  fixedMinor = 0,
): (number | null)[] {
  const sign = baseMinor < 0 ? -1 : 1;
  const abs = Math.abs(baseMinor);
  let fixed = fixedMinor;
  let percent = 0;
  const out: (number | null)[] = rows.map(() => null);
  rows.forEach((_row, i) => {
    const slice = parsed[i] ?? null;
    if (slice === null || slice.kind === 'rest') return;
    const amount =
      slice.kind === 'amount'
        ? slice.amountMinor
        : sign * Math.floor((abs * slice.percentBp) / 10000);
    out[i] = amount;
    if (slice.kind === 'amount') fixed += amount;
    else percent += amount;
  });
  const rest = rows.findIndex((_row, i) => parsed[i]?.kind === 'rest');
  if (rest >= 0) out[rest] = baseMinor - fixed - percent;
  return out;
}

export function publishedTotal(previews: readonly (number | null)[]): number {
  return previews.reduce<number>((sum, value) => sum + (value ?? 0), 0);
}

/** The row's category as a request carries it. */
function categoryOf(row: AllocateRow): Pick<AllocationLineRequest, 'categoryId' | 'uncategorized'> {
  if (typeof row.categoryId === 'number') return { categoryId: row.categoryId };
  return row.categoryId === UNCATEGORIZED ? { uncategorized: true as const } : {};
}

function slice(parsedSlice: Exclude<ParsedSlice, null>) {
  if (parsedSlice.kind === 'amount') return { amountMinor: parsedSlice.amountMinor } as const;
  if (parsedSlice.kind === 'percent') return { percentBp: parsedSlice.percentBp } as const;
  return { remainder: true } as const;
}

export function toLineRequest(
  row: AllocateRow,
  parsedSlice: Exclude<ParsedSlice, null>,
): AllocationLineRequest {
  return {
    ...(row.envelopeId === null
      ? { virtualAccountId: row.virtualAccountId }
      : { envelopeId: row.envelopeId }),
    ...slice(parsedSlice),
    ...categoryOf(row),
  };
}

export function toSplitPart(
  row: AllocateRow,
  parsedSlice: Exclude<ParsedSlice, null>,
): SplitPartRequest {
  return {
    ...(row.envelopeId === null ? { unassigned: true as const } : { envelopeId: row.envelopeId }),
    ...slice(parsedSlice),
    ...categoryOf(row),
  };
}

/** One virtual account's edit: its new total (the previews' sum), then its parts as amounts. */
export interface GroupEdit {
  virtualAccountId: number;
  /** The line's live total in this virtual account before the edit. */
  currentMinor: number;
  /** The group's intended total, from the previews. */
  totalMinor: number;
  /** The parts, as previewed amounts (a split resolves percents against its own base). */
  parts: SplitPartRequest[];
  /** The rows behind `parts`, in order (for `resolved[]` / `parts[i]` errors). */
  rowKeys: string[];
  /** False when the parts equal the live ones: nothing to write. */
  changed: boolean;
}

export interface WritePlan {
  /** Lines for the virtual accounts the line is new to: one `post`, with its rows in order. */
  post: { lines: AllocationLineRequest[]; rowKeys: string[] };
  /**
   * Virtual accounts the line is already in, reductions and unchanged totals first, increases
   * last: `total` when the total changed, then `split` when the parts changed.
   */
  edits: GroupEdit[];
  /** Virtual accounts whose every row was removed: `total` to zero. */
  cleared: { virtualAccountId: number; currentMinor: number }[];
}

/** Does the plan write anything? */
export function planIsEmpty(plan: WritePlan): boolean {
  return (
    plan.post.lines.length === 0 &&
    !plan.edits.some((edit) => edit.changed) &&
    plan.cleared.length === 0
  );
}

function partKey(
  target: { envelopeId: number | null; unassigned: boolean },
  amountMinor: number,
  categoryId: number | null,
): string {
  return `${target.unassigned ? 'u' : target.envelopeId}:${amountMinor}:${categoryId ?? '-'}`;
}

/**
 * Which route each group takes. `editable` is the line's editable live parts; `locked` the virtual
 * accounts holding a fixed part (never written); `wholeLine` says the line holds nothing but these
 * rows, so a post may carry the typed percent / rest.
 */
export function planWrites(
  rows: readonly AllocateRow[],
  parsed: readonly ParsedSlice[],
  previews: readonly (number | null)[],
  editable: readonly PlacedPart[],
  locked: ReadonlySet<number>,
): WritePlan {
  const live = new Map<number, PlacedPart[]>();
  for (const placed of editable) {
    if (placed.virtualAccountId === null) continue;
    live.set(placed.virtualAccountId, [...(live.get(placed.virtualAccountId) ?? []), placed]);
  }
  const wholeLine = editable.length === 0 && locked.size === 0;
  const groups = new Map<number, number[]>();
  rows.forEach((row, i) => {
    if (parsed[i] === null || parsed[i] === undefined || locked.has(row.virtualAccountId)) return;
    groups.set(row.virtualAccountId, [...(groups.get(row.virtualAccountId) ?? []), i]);
  });
  const post: WritePlan['post'] = { lines: [], rowKeys: [] };
  const edits: GroupEdit[] = [];
  for (const [virtualAccountId, indexes] of groups) {
    const current = live.get(virtualAccountId);
    if (current === undefined) {
      for (const i of indexes) {
        const row = rows[i]!;
        const amount = previews[i];
        post.lines.push(
          wholeLine || amount === null || amount === undefined
            ? toLineRequest(row, parsed[i]!)
            : toLineRequest(row, { kind: 'amount', amountMinor: amount }),
        );
        post.rowKeys.push(row.key);
      }
      continue;
    }
    const currentMinor = current.reduce((sum, placed) => sum + placed.part.amountMinor, 0);
    const parts = indexes.map((i) =>
      toSplitPart(rows[i]!, { kind: 'amount', amountMinor: previews[i] ?? 0 }),
    );
    const liveKeys = current
      .map(({ part }) => partKey(part, part.amountMinor, part.categoryId))
      .sort();
    const nextKeys = indexes
      .map((i) =>
        partKey(
          { envelopeId: rows[i]!.envelopeId, unassigned: rows[i]!.envelopeId === null },
          previews[i] ?? 0,
          typeof rows[i]!.categoryId === 'number' ? rows[i]!.categoryId : null,
        ),
      )
      .sort();
    edits.push({
      virtualAccountId,
      currentMinor,
      totalMinor: indexes.reduce((sum, i) => sum + (previews[i] ?? 0), 0),
      parts,
      rowKeys: indexes.map((i) => rows[i]!.key),
      changed: liveKeys.join('|') !== nextKeys.join('|'),
    });
  }
  // Reductions and unchanged totals first; increases once the line has room (I-1).
  edits.sort((a, b) => {
    const grows = (edit: GroupEdit) =>
      Math.abs(edit.totalMinor) > Math.abs(edit.currentMinor) ? 1 : 0;
    return grows(a) - grows(b);
  });
  const cleared = [...live]
    .filter(([virtualAccountId]) => !groups.has(virtualAccountId) && !locked.has(virtualAccountId))
    .map(([virtualAccountId, parts]) => ({
      virtualAccountId,
      currentMinor: parts.reduce((sum, placed) => sum + placed.part.amountMinor, 0),
    }));
  return { post, edits, cleared };
}
